import { useState, useRef, useLayoutEffect } from "react";
// --- gsap ----------------------------------------------------------------
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// --- swiper ----------------------------------------------------------------
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
// --- lottie ----------------------------------------------------------------
import Lottie from "lottie-react";
import contactLogo from "../assets/lotties/logo-purple.json";
// --- img ----------------------------------------------------------------
import {
  kakaoIcon,
  snsInstaLogo,
  snsTiktokLogo,
  snsYoutubeLogo,
  recruitFree01,
  recruitFree02,
  recruitFree03,
  recruitFree04,
  recruitFree05,
  recruitFree06,
  recruitFree07,
  recruitFree08,
  recruitBrand01,
  recruitBrand02,
  recruitBrand03,
  recruitBrand04,
  recruitBrand05,
  recruitBrand06,
} from "../assets/images";
// --- style ----------------------------------------------------------------
import "../styles/pages/Recruit.scss";

gsap.registerPlugin(ScrollTrigger);

function Recruit() {
  // --- sns ticker ----------------------------------------------------------------
  const snsIcons = [
    { src: snsInstaLogo, alt: "Instagram" },
    { src: snsYoutubeLogo, alt: "YouTube" },
    { src: snsTiktokLogo, alt: "TikTok" },
  ];  
  const repeated = Array.from({ length: 8 }, (_, i) =>
    snsIcons.map((it, idx) => ({ ...it, key: `${i}-${idx}` }))
  ).flat();  

  // --- profile swiper ----------------------------------------------------------------
  const profileItems = [
    {
      key: "artist",
      label: "보컬 아티스트 형",
      desc: "실력과 매력을 무대로 표현할 수 있는 사람",
    },    
    {
      key: "performance",
      label: "댄서·퍼포먼스 형",
      desc: "영상·무대 중심의 팀형 프로젝트 참여 가능자",
    },
    {
      key: "creator",
      label: "콘텐츠 크리에이터 형",
      desc: "AI 영상·숏폼·브랜디드 콘텐츠 기획·출연 가능자",
    },
    {
      key: "musical",
      label: "웹드라마 / 뮤지컬 형",
      desc: "연기·연출·음악을 결합한 프로젝트 참여 가능자",
    },
    {
      key: "idea",
      label: "아이디어 메이커 형",
      desc: "새로운 기획·콜라보·브랜드 제안이 가능한 인재",
    },     
  ]  
  const [activeKey, setActiveKey] = useState(profileItems[0].key);
  const activeItem = profileItems.find((item) => item.key === activeKey) ?? profileItems[0];  

  // --- partnership ----------------------------------------------------------------
  const benefits = [
    {
      id: "studio",
      title: "스튜디오 이용 혜택",
      desc: "녹음실 / 촬영실 / 라운지 무상 또는 할인 혜택 제공",
    },
    {
      id: "revenue",
      title: "수익 공유 시스템",
      desc: "유통 광고 · 공연 등 수익 구조 연동",
    },
    {
      id: "membership",
      title: "카페존 멤버십",
      desc: "MOB 카페 라운지 이용 + 협업 미팅 공간 제공",
    },
    {
      id: "global",
      title: "글로벌 진출 지원",
      desc: "해외 방송 · 틱톡 · 유튜브 글로벌 협업 연계",
    },
    {
      id: "profile",
      title: "프로필 · 콘텐츠 제작 지원",
      desc: "아티스트 프로필 / 포트폴리오 영상 지원",
    },
    {
      id: "priority",
      title: "행사 · 광고 우선 배정",
      desc: "브랜드 광고, 이벤트, 행사 우선 참여 기회",
    },
  ];

  // --- 애니메이션
  const benefitsRef = useRef(null);

  useLayoutEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const items = el.querySelectorAll(":scope > li");

      gsap.set(items, { autoAlpha: 0, y: 24 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true,
        },
      });

      items.forEach((item, i) => {
        tl.to(
          item,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
          },
          i === 0 ? 0 : "-=0.45" // 이어짐 조절
        );
      });
    }, benefitsRef);

    return () => ctx.revert();
  }, []);


  return (
    <div className='page-wrapper recruit'>
      <div className="content-section__title">
        <small className='recruit-desc'>우리는 무대를 만드는 사람들입니다</small>
        <small className='recruit-desc'>음악, 영상, 광고 라이브, 웹드라마까지</small>
        <p>MOB STUDIO는 다양한 플랫폼과 장르를 넘나드는 <span className="recruit-spacing"><span>크리에이티브 집합체(Creative Collective)</span>입니다</span></p>
        <h3 className="gfont">GROW with MOB STUDIO</h3>
      </div>

      <div className="content-section">
        {/* === SOCIAL TICKER === */}
        <div className="social-ticker">
          <div className="social-ticker__viewport">
            <div className="social-ticker__track">
              <ul className="social-ticker__group">
                {repeated.map((it) => (
                  <li key={it.key}>
                    <img src={it.src} alt={it.alt} draggable="false" />
                  </li>
                ))}
              </ul>

              <ul className="social-ticker__group" aria-hidden="true">
                {repeated.map((it) => (
                  <li key={`clone-${it.key}`}>
                    <img src={it.src} alt="" aria-hidden="true" draggable="false" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* === IMAGE LIST === */}
        <ul className="recruit-image-list max-12">
          <li>#팀형 콘텐츠 제작</li>
          <li>#댄스 라이브 공연</li>
          <li>#웹드라마,뮤지컬 기반 콘텐츠</li>
          <li>#AI음악 / 광고송 / 브랜드콘텐츠</li>
          <li>#라이브 스트리밍 방송</li>
        </ul>

        {/* === CREATOR PROFILE === */}
        <section className="soundlab__step max-12">
          <div className="content-section__title">
            <span>MOB 크리에이터 인재상</span>
            <h3 className="gfont">CREATOR PROFILE</h3>
          </div>        
          <div className="step-bg oval">
            <div className="step-swiper-wrap">
              <Swiper
                className="step-swiper"
                slidesPerView="auto"
                freeMode
                modules={[FreeMode]}
                spaceBetween={12}
                preventClicks={true}
                touchStartPreventDefault={false}
                noSwiping={false}
              >
                {profileItems.map((item, idx) => (
                  <SwiperSlide key={item.key} style={{ width: "auto" }}>
                    <button
                      type="button"
                      className={activeKey === item.key ? "active" : ""}
                      onClick={() => setActiveKey(item.key)}
                    >
                      {item.label}
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>  
            </div>  
            <div className="step-content">
              <div className="content-bg">
                <p className="desc-03">{activeItem.desc}</p>
              </div>
            </div>
          </div>    
        </section>   

        {/* === FREELANCER ROLES === */}
        {/* https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325 */}
        <section className="recruit-image-scale max-12">
          <div className="content-section__title">
            <span>MOB 프리랜서 인재상</span>
            <h3 className="gfont">FREELANCER ROLES</h3>
          </div>
          <ul className="free">
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree01})` }}
                  aria-hidden="true"
                />
                <h4>
                  AI · 음악 제작
                  <span>
                    ai작사 · 작곡, 사운드 디자인<br />
                    SUNO / chat gpt 기반 ai 음악 실무 / 레슨
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree02})` }}
                  aria-hidden="true"
                />
                <h4>
                  작곡가 / 작사가
                  <span>
                    오리지널 곡 제작, 보컬 디렉팅<br />
                    MOB 음원 / 광고송 제작 참여 / 레슨
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree03})` }}
                  aria-hidden="true"
                />
                <h4>
                  가수 / 보컬리스트
                  <span>
                    음원 녹음 · 커버송 / 라이브 공연<br />
                    AI 음악 콜라보 / 뮤직비디오 출연 / 레슨
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree04})` }}
                  aria-hidden="true"
                />
                <h4>
                  댄서 / 안무가
                  <span>
                    안무 제작 및 공연 촬영<br />
                    AI무비 / 숏폼 콘텐츠 안무 제작 레슨
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree05})` }}
                  aria-hidden="true"
                />
                <h4>
                  AI 영상제작자
                  <span>
                    AI 툴 활용 영상 편집 및 기획 제작<br />
                    AI 비주얼 뮤직 비디오 제작
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree06})` }}
                  aria-hidden="true"
                />
                <h4>
                  PD · 방송작가
                  <span>
                    콘텐츠 촬영 기획 편집
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree07})` }}
                  aria-hidden="true"
                />
                <h4>
                  디자이너 / 그래픽
                  <span>
                    썸네일 / 브랜딩 / 포스터 디자인<br />
                    콘텐츠 비주얼 및 패키징
                  </span>
                </h4>
              </a>              
            </li>
            <li>
              <a
                href="https://www.jobkorea.co.kr/Recruit/Co_Read/Recruit/C/35876325"
                className="scale-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitFree08})` }}
                  aria-hidden="true"
                />
                <h4>
                  마케팅 · SNS 매니저
                  <span>
                    광고 기획 / 채널 운영 / 리포팅<br />
                    인플루언서 협업 / 브랜드 관리
                  </span>
                </h4>
              </a>              
            </li>
          </ul>        
        </section>       

        {/* === PARTNERSHIP === */}
        <section className="recruit-partnership">
          <div className="content-section__title dark-gradient max-12">
            <span>MOB은 크리에이터가 ‘소속이 아닌, <span className="spacing">파트너’로 성장하는 구조입니다</span></span>
            <h3 className="gfont">MOB PARTERSHIP STRUCTURE & BENEFITS</h3>
          </div>
          <div className="structure">
            <dl className="desc-01">
              <dt>Creator</dt>
              <dd>콘텐츠 기획 / 촬영 / 유통 공동 참여</dd>
            </dl>
            <dl className="desc-02">
              <dt>Brand</dt>
              <dd>광고 · 협찬 · 콜라보 프로젝트<br />직접 매칭</dd>
            </dl>
            <dl className="desc-03">
              <dt>Instructor</dt>
              <dd>교육 + 제작 + 유통이 연결되는 통합 생태계</dd>
            </dl>
          </div>   
          <div className="benefits max-12">
            <h4>BENEFITS</h4>
            <ul className="benefits-list" ref={benefitsRef}>
              {benefits.map((item) => (
                <li key={item.id}>
                  <h5>{item.title}</h5>
                  <p>{item.desc}</p>
                </li>
              ))}
            </ul>            
          </div>
        </section>

        {/* === BRAND PARTNERSHIP === */}
        <section className="recruit-image-scale max-12">
          <div className="content-section__title">
            <span>맞춤형 광고 협업 서비스</span>
            <h3 className="gfont">BRAND PARTNERSHIP</h3>
          </div>
          <ul className="brand">
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand01})` }}
                  aria-hidden="true"
                />
                <h4>
                  맞춤형 영상광고 제작
                  <span>
                    인플루언서 광고<br />
                    브랜드 콘셉트에 맞춘 촬영형 광고                    
                  </span>
                </h4>
              </div>              
            </li>
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand02})` }}
                  aria-hidden="true"
                />
                <h4>
                  콘텐츠 제작 패키지
                  <span>
                    음악 + 영상 + SNS 세트형 콘텐츠 제작
                  </span>
                </h4>
              </div>              
            </li>
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand03})` }}
                  aria-hidden="true"
                />
                <h4>
                  채널 운영 대행
                  <span>
                    채널 컨설팅 브랜딩/ 브랜드 계정 /<br />
                    인플루언서 계정 운영 지원                    
                  </span>
                </h4>
              </div>              
            </li>
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand04})` }}
                  aria-hidden="true"
                />
                <h4>
                  행사·버스킹 프로모션 제휴
                  <span>
                    브랜드 현장 공연<br />
                    팝업 협업 이벤트
                  </span>
                </h4>
              </div>              
            </li>
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand05})` }}
                  aria-hidden="true"
                />
                <h4>
                  아티스트 협찬 & 콜라보
                  <span>
                    브랜드송, 협찬 캠페인, 굿즈 제작 등
                  </span>
                </h4>
              </div>              
            </li>
            <li>
              <div className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${recruitBrand06})` }}
                  aria-hidden="true"
                />
                <h4>
                  브랜드의 스토리를<br />
                  음악과 영상으로 표현합니다
                </h4>
              </div>              
            </li>
          </ul>        
        </section>         
        
        {/* === INQUIRY === */} 
        <div className="content-section__contact">
          <p className="desc">
            ALL MAKERS, ONE STAGE. 지금 당신의 무대를 시작하세요
          </p>
          <h3 className="gfont title">
            CONTACT US
          </h3>
          <Lottie
            animationData={contactLogo}
            loop
            autoplay
            className="contact-lottie"
          />

          <div className="kakao-link">
            <span>지금 시작해 보세요.</span>
            <a 
            href="https://pf.kakao.com/_QLTxgn"
            target="_blank"
            rel="noopener noreferrer"
            >
              <img src={kakaoIcon} alt="" aria-hidden="true" />
              카카오 채널 문의
            </a>
          </div>
        </div> 
      </div>          
    </div>
  )
}

export default Recruit